/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { DiscountLine, PeriodicDiscountOfferType, PriceLine, ProductPrice, ProductType, SalesAgreementPriceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { Button, Collapse, format, ICollapseProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { ProductPriceExt } from '../../../../shared-utils/types';
import { formatCurrencyNOK } from '../../../../shared-utils/common-utils';

const defaultPrice: number = 0;
const freePrice: number = 0;

/**
 * Interface for price component resources.
 * @param {string} priceRangeSeparator - The price range separator.
 */
export interface IPriceComponentResources {
    priceRangeSeparator?: string;
    salesAgreementPricePrompt?: string;
    salesAgreementExpirationDatePrompt?: string;
    salesAgreementCommittedQuantityPrompt?: string;
    salesAgreementRemainingQuantityPrompt?: string;
}

/**
 * Interface for price component props.
 * @param {boolean} isPriceMinMaxEnabled - Whether the price range feature is enabled or not.
 * @param {IPriceComponentResources} priceResources - The price range resources.
 */
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    product?: SimpleProduct;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    isPriceMinMaxEnabled?: boolean;
    isSalesAgreementPriceFeatureEnabled?: boolean;
    useLegacySalesAgreementHTMLFeature?: boolean;
    salesAgreementLineId?: number;
    priceResources?: IPriceComponentResources;
    salesAgreementDetailCollapseProps?: ICollapseProps;
    currencyCode?: string;
    salesAgreementDetailOnClick?(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
    discountPercentageSide?: 'left' | 'right';
    hideBeforePrice?: boolean;
}

/**
 * The interface of price component.
 */
export interface IPriceComponent extends IComponent<IPriceComponentProps> {}

const priceComponentActions = {};

/**
 * Get the original price from price component props.
 * @param props - The IPriceComponentProps.
 * @returns The original price.
 */
export const getOriginalPrice = (price: ProductPrice): number => {
    let originalPrice = Math.max(
        price.TradeAgreementPrice || defaultPrice,
        price.AdjustedPrice || defaultPrice
    ) || price.BasePrice || defaultPrice;

    const hasMixAndMatchDiscount = price.DiscountLines
        ?.some(discount => discount.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.MixAndMatch);

    // Go through each discount line and apply discounts without "Show" in it
    getBaseDiscountLines(price.DiscountLines).forEach(discount => {
        // When there is a mix and match discount, we apply the effective percentage because it is more accurate
        // than the effective amount. This is because customer agreements wont apply on the free item and messes up the calculation.
        if (hasMixAndMatchDiscount) {
            originalPrice *= (1 - (discount.EffectivePercentage ?? 0) / 100);
        } else {
            originalPrice -= discount.EffectiveAmount ?? 0;
        }
    });

    return originalPrice;
};

export const getDiscountLines = (discountLines: DiscountLine[] | undefined) => {
    if (!discountLines || discountLines.length === 0) {
        return [];
    }

    // Go through each discount line and find lines with Show in it
    const discounts = discountLines.filter(x => {
        const matches = x.OfferName?.toLowerCase().match(/\{(.*?)\}/i) || '';
        const parts = matches.length > 1 ? matches[1].split(';').map(x => x.trim()) : undefined;
        return parts && parts.includes('show');
    });

    return discounts;
};

export const getBaseDiscountLines = (discountLines: DiscountLine[] | undefined) => {
    if (!discountLines || discountLines.length === 0) {
        return [];
    }

    // Go through each discount line and apply discounts without "Show" in it
    const baseDiscounts = discountLines.filter(x => {
        const matches = x.OfferName?.toLowerCase().match(/\{(.*?)\}/i) || '';
        const parts = matches.length > 1 ? matches[1].split(';').map(x => x.trim()) : undefined;
        return !parts || parts.length === 0 || !parts.includes('show');
    });

    return baseDiscounts;
};

export const getDiscountPercentage = (price: ProductPriceExt): number => {
    if (price.OverrideDiscountPercentage) {
        return price.OverrideDiscountPercentage;
    }

    if (!price.CustomerContextualPrice) {
        return 0;
    }

    const originalPrice = getOriginalPrice(price);
    const percentage = 1 - (price.CustomerContextualPrice / originalPrice);

    return Math.round(percentage * 100);

    // Go through each discount line and find lines with Show in it
    // METHOD: Shows the percentage defined in F&O directly
    /* const discounts = price.DiscountLines?.filter(x => {
        const matches = x.OfferName?.toLowerCase().match(/\{(.*?)\}/i) || '';
        const parts = matches.length > 1 ? matches[1].split(';').map(x => x.trim()) : undefined;
        return parts && parts.includes('show');
    });

    return discounts?.reduce((acc, discount) => {
        // First use 'Percentage' if it exists, then 'EffectivePercentage' if it exists, then 0
        return acc + (discount.Percentage || Math.floor(discount.EffectivePercentage || 0) || 0);
    }, 0) ?? 0; */

    // Go through each discount line and find lines with Show in it
    // METHOD: Tries to get the percentage defined in F&O. Inaccurate
    /* const originalPrice = Math.max(
        price.BasePrice ?? defaultPrice,
        price.TradeAgreementPrice ?? defaultPrice,
        price.AdjustedPrice ?? defaultPrice
    );
    const discounts = price.DiscountLines?.filter(x => {
        const matches = x.OfferName?.toLowerCase().match(/\{(.*?)\}/i) || '';
        const parts = matches.length > 1 ? matches[1].split(';').map(x => x.trim()) : undefined;
        return parts && parts.includes('show');
    });

    return discounts?.reduce((acc, discount) => {
        if (!discount.EffectiveAmount) {
            return acc;
        }

        return acc + Math.round(((discount.EffectiveAmount / originalPrice) * 100) + 0.2);
    }, 0) ?? 0; */
};

const getActivePrice = (props: IPriceComponentProps): number => {
    if (showPriceRangeForMasterProduct(props) && props.data.price.MinVariantPrice !== undefined) {
        return props.data.price.MinVariantPrice;
    }

    if (props.data.price.CustomerContextualPrice !== undefined) {
        return props.data.price.CustomerContextualPrice;
    }

    return getOriginalPrice(props.data.price);
};

/**
 * Get the formatted currency.
 * @param props - The props.
 * @param price - The price.
 * @param shouldUseFreePriceText - The shouldUseFreePriceText.
 * @returns The formatted currency.
 */
const formatCurrency = (
    props: IPriceComponentProps,
    price: number | undefined,
    shouldUseFreePriceText: boolean | undefined,
    currencyCode?: string | undefined
): string => {
    if (price === undefined) {
        return '';
    }

    if (shouldUseFreePriceText && price === freePrice && props.freePriceText) {
        return props.freePriceText;
    }

    if (!currencyCode || currencyCode === 'NOK') {
        return formatCurrencyNOK(price);
    } else {
        return props.context.cultureFormatter.formatCurrency(price, currencyCode);
    }
};

const showPriceRangeForMasterProduct = (props: IPriceComponentProps): boolean => {
    return (
        (props.isPriceMinMaxEnabled ?? false) &&
        (props.product?.ProductTypeValue === ProductType.Master || props.product?.ProductTypeValue === ProductType.KitMaster) &&
        props.data.price.MinVariantPrice !== undefined &&
        props.data.price.MaxVariantPrice !== undefined
    );
};

/**
 * ShowStrikethroughPricing.
 * @param props - The props.
 * @returns True if showStrikethroughPricing, false otherwise.
 */
const showStrikethroughPricing = (props: IPriceComponentProps): boolean => {
    const price = props.data.price as ProductPriceExt;
    const overrideDiscountPercentage = price.OverrideDiscountPercentage;

    if (!overrideDiscountPercentage && getDiscountLines(price.DiscountLines).length === 0) {
        return false;
    }

    if (overrideDiscountPercentage) {
        return overrideDiscountPercentage > 0;
    }

    const originalPrice = getOriginalPrice(price);

    if (originalPrice && price.CustomerContextualPrice) {
        const originalPriceFixed = parseFloat(originalPrice.toFixed(2));
        const customerContextualPriceFixed = parseFloat(price.CustomerContextualPrice.toFixed(2));
        return originalPriceFixed > customerContextualPriceFixed;
    }

    return false;
};

/**
 * Render current price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element => {
    const maxVariantPrice: string = formatCurrency(props, props.data.price.MaxVariantPrice, false, props.currencyCode);
    const minVariantPrice: string = formatCurrency(props, props.data.price.MinVariantPrice, false, props.currencyCode);
    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        return (
            <span className='msc-price__pricerange' itemProp='price'>
                <span className='msc-price__minprice' itemProp='price'>
                    {minVariantPrice}
                </span>
                <span className='msc-price__separator' itemProp='price'>
                    {props.priceResources?.priceRangeSeparator}
                </span>
                <span className='msc-price__maxprice' itemProp='price'>
                    {maxVariantPrice}
                </span>
            </span>
        );
    }

    return (
        <span className='msc-price__actual' itemProp='price'>
            {formatCurrency(props, getActivePrice(props), true, props.currencyCode)}
        </span>
    );
};

/**
 * Render current price with original price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element => {
    const originalPrice: number = getOriginalPrice(props.data.price);
    const initialPrice: string = formatCurrency(props, originalPrice, true);
    const discountPercentage = getDiscountPercentage(props.data.price);

    return (
        <>
            <span className='sr-only'>
                {` `}
                {props.originalPriceText}
                {` `}
                {initialPrice}
                {` `}
                {props.currentPriceText}
                {` `}
                {renderCurrentPrice(props)}
            </span>
            <span aria-hidden='true'>
                {renderCurrentPrice(props)}
                {!props.hideBeforePrice && (
                    <span className='msc-price__strikethrough' aria-hidden='true'>
                        {initialPrice}
                    </span>
                )}
            </span>
            {discountPercentage > 0 && <span className='msc-price__discount-percentage'>-{discountPercentage}%</span>}
            {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
        </>
    );
};

/**
 * The state of sales agreement details.
 * @deprecated This will be removed with SalesAgreementPrice class soon.
 */
export interface ISalesAgreementDetailState {
    isOpen: boolean;
}

/**
 * The SalesAgreementPrice class.
 * @deprecated This implementation is deprecated. It will be removed soon. Please use the implementation in the Price function component.
 */
// eslint-disable-next-line react/require-optimization -- Not needed.
class SalesAgreementPrice extends React.Component<IPriceComponentProps, ISalesAgreementDetailState> {
    public constructor(props: IPriceComponentProps) {
        super(props);
        this._toggle = this._toggle.bind(this);
        this.state = {
            isOpen: true
        } as ISalesAgreementDetailState;
    }

    public render(): React.ReactElement {
        const priceLines: PriceLine[] = this.props.data.price.AttainablePriceLines ?? [];
        const firstPriceLine: SalesAgreementPriceLine = priceLines[0] as SalesAgreementPriceLine;
        if (this.props.salesAgreementDetailCollapseProps) {
            this.props.salesAgreementDetailCollapseProps.isOpen = this.state.isOpen;
        }
        return (
            <span className={classnames('msc-price__salesagreementprice', this.props.className)}>
                <p className='msc-price__actual' itemProp='price'>
                    {formatCurrency(
                        this.props,
                        this.props.data.price.SalesAgreementPrice ?? this.props.data.price.CustomerContextualPrice,
                        true
                    )}
                </p>
                <p
                    className={this.state.isOpen ? 'msc-price__salesagreementprompt_expanded' : 'msc-price__salesagreementprompt_collapsed'}
                    onClick={this._toggle}
                >
                    {this.props.priceResources?.salesAgreementPricePrompt}
                </p>
                <Collapse {...this.props.salesAgreementDetailCollapseProps}>
                    <p className={classnames('msc-price__salesagreementdetail', this.props.className)}>
                        <span>
                            {/* Available date */}
                            {// eslint-disable-next-line prefer-template -- Copied from other class.
                            ' · ' +
                                format(
                                    this.props.priceResources?.salesAgreementExpirationDatePrompt ?? '',
                                    this.props.context.cultureFormatter.formatDate(firstPriceLine.EffectiveDate as Date),
                                    this.props.context.cultureFormatter.formatDate(firstPriceLine.ExpirationDate as Date)
                                )}
                        </span>
                        <span>
                            {/* Quantity commitment */}
                            {// eslint-disable-next-line prefer-template -- Copied from other class.
                            ' · ' +
                                format(
                                    this.props.priceResources?.salesAgreementCommittedQuantityPrompt ?? '',
                                    firstPriceLine.CommittedQuantity
                                )}
                        </span>
                        <span>
                            {/* Remaining quantity to qualify */}
                            {// eslint-disable-next-line prefer-template -- Copied from other class.
                            ' · ' +
                                format(
                                    this.props.priceResources?.salesAgreementRemainingQuantityPrompt ?? '',
                                    firstPriceLine.RemainingQuantity
                                )}
                        </span>
                    </p>
                </Collapse>
            </span>
        );
    }

    /**
     * Callback which toggles collapse mode.
     * @param event - The mouse or keyboard event.
     */
    private _toggle(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        event.preventDefault();
        console.log(this.state.isOpen);
        // eslint-disable-next-line react/no-access-state-in-setstate -- Copied from other class.
        this.setState({ isOpen: !this.state.isOpen });
        console.log(this.state.isOpen);
    }
}

const renderSalesAgreementDetail = (props: IPriceComponentProps): JSX.Element => {
    // In most cases there should be only one sales agreement line.
    // If there are more than one, only the first one will be shown.
    const firstPriceLine: SalesAgreementPriceLine = (props.data.price.AttainablePriceLines ?? [])[0] as SalesAgreementPriceLine;
    return (
        <p className={classnames('msc-price__salesagreementdetail', props.className)}>
            <p>
                {/* Available date */}
                {// eslint-disable-next-line prefer-template -- Copied from other class.
                ' · ' +
                    format(
                        props.priceResources?.salesAgreementExpirationDatePrompt ?? '',
                        props.context.cultureFormatter.formatDate(firstPriceLine.EffectiveDate as Date),
                        props.context.cultureFormatter.formatDate(firstPriceLine.ExpirationDate as Date)
                    )}
            </p>
            <p>
                {/* Quantity commitment */}
                {// eslint-disable-next-line prefer-template -- Copied from other class.
                ' · ' + format(props.priceResources?.salesAgreementCommittedQuantityPrompt ?? '', firstPriceLine.CommittedQuantity)}
            </p>
            <p>
                {/* Remaining quantity to qualify */}
                {// eslint-disable-next-line prefer-template -- Copied from other class.
                ' · ' + format(props.priceResources?.salesAgreementRemainingQuantityPrompt ?? '', firstPriceLine.RemainingQuantity)}
            </p>
        </p>
    );
};

/**
 * Price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps): JSX.Element | null => {
    // CustomerContextualPrice could be zero
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    if (!props.context.request.user.isAuthenticated) {
        return null;
    }

    // Determine if contract price is avaiable/applied by:
    //   isFeatureEnabled and SA price != 0, for PDP.
    //   SA line id != 0, for cart line.
    if (props.isSalesAgreementPriceFeatureEnabled && props.data.price.SalesAgreementPrice !== 0) {
        if (props.useLegacySalesAgreementHTMLFeature) {
            const salesAgreementPrice = new SalesAgreementPrice(props);
            return <span className={classnames('msc-price', props.className)}>{salesAgreementPrice.render()}</span>;
        } else {
            const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
            const toggle = () => {
                setIsExpanded(!isExpanded);
            };
            return (
                <span className={classnames('msc-price', props.className)}>
                    <span className={classnames('msc-price__salesagreementprice', props.className)}>
                        <p className='msc-price__actual' itemProp='price'>
                            {formatCurrency(props, props.data.price.SalesAgreementPrice ?? props.data.price.CustomerContextualPrice, true)}
                        </p>
                        <Button
                            className={
                                isExpanded ? 'msc-price__salesagreementprompt_expanded' : 'msc-price__salesagreementprompt_collapsed'
                            }
                            aria-label={props.priceResources?.salesAgreementPricePrompt}
                            onClick={toggle}
                            aria-expanded={isExpanded}
                            tabIndex={0}
                        >
                            {props.priceResources?.salesAgreementPricePrompt}
                        </Button>
                        {isExpanded && renderSalesAgreementDetail(props)}
                    </span>
                </span>
            );
        }
    }

    const discountPercentageSide = props.discountPercentageSide ?? 'right';

    return (
        <span className={classnames('msc-price', props.className, `discount-percentage-${discountPercentageSide}`)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
            {/* {renderCurrentPrice(props)} */}
        </span>
    );
};

/**
 * The PriceComponent.
 */
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<IPriceComponent>('Price', {
    component: Price,
    ...priceComponentActions
});


export default PriceComponent;